import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";
import Testimonials from "@/components/Testimonials";

const offerings = [
  {
    n: "01",
    title: "One-to-One Coaching",
    tag: "For individuals in the messy middle",
    copy: "A safe, honest space to pause, name where you really are, and find your next courageous step. Sessions of 60–90 minutes, shaped around your season — reflective when you need room, practical when you need handles.",
    points: ["A safe space that isn't passive", "Practical tools, not just inspiration", "Accountability with warmth"],
    to: "/contact",
    cta: "Start a conversation",
  },
  {
    n: "02",
    title: "The Messy Middle",
    tag: "The signature 10-session journey",
    copy: "Alex's flagship coaching journey through disruption — individually or in groups. Ten sessions that move from naming where you are to living into the Not Yet, combining coaching, facilitation, journaling, reflection and honest discussion.",
    points: ["10 sessions, 60–90 minutes each", "Individual or group journeys", "Reframe → Repurpose → Pivot → Grow"],
    to: "/messy-middle",
    cta: "Explore the journey",
  },
  {
    n: "03",
    title: "Leadership Development",
    tag: "For leaders who grow others",
    copy: "Leadership begins with self-leadership — moving from “I deserve to lead” to “I get to lead.” Coaching and training built on responsibility, intentionality, communication and connection, from a Maxwell Leadership certified trainer.",
    points: ["Lead yourself first", "Communication that actually connects", "Grow the people who grow the work"],
    to: "/contact",
    cta: "Grow as a leader",
  },
];

const before = [
  "Hopeless or battered by the season",
  "Confused about what comes next",
  "Impatient, frustrated by non-outcomes",
  "Trapped in self-limiting beliefs",
  "Uncertain of the next step",
];

const after = [
  "Supported, seen and cheered on",
  "Clearer, calmer, more grounded",
  "Equipped with practical tools",
  "More hopeful — and more courageous",
  "Willing to have another go at life",
];

export default function WorkWithAlex() {
  useEffect(() => {
    document.title = "Work With Alex — People Growing People";
  }, []);

  return (
    <main data-testid="work-with-alex-page" className="pt-20">
      {/* HERO */}
      <section className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="text-xs uppercase tracking-[0.3em] text-stone">Work With Alex</span>
            </MaskedLine>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] mt-6">
              <MaskedLine delay={0.25}>You don't have to</MaskedLine>
              <MaskedLine delay={0.37}>
                figure this out <span className="italic text-clay">alone.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.55}>
              <p className="mt-10 text-base md:text-lg leading-relaxed text-ink/75 max-w-xl">
                This is an invitation to begin a conversation — not a funnel. Three
                ways we can work together, depending on where you are and where
                you're hoping to go.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.3}>
              <div className="overflow-hidden border border-line">
                <img
                  src="/alex-window.jpg"
                  alt="Alex sitting quietly by a rain-streaked window, looking out"
                  className="w-full aspect-[3/4] object-cover"
                  data-testid="work-hero-photo"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section data-testid="offerings-section" className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {offerings.map((o, i) => (
            <Reveal key={o.n}>
              <div
                data-testid={`offering-${i + 1}`}
                className={`grid md:grid-cols-12 gap-8 md:gap-12 py-16 md:py-24 ${i > 0 ? "border-t border-line" : ""}`}
              >
                <div className="md:col-span-2">
                  <span className="font-serif text-5xl md:text-6xl text-clay/60">{o.n}</span>
                </div>
                <div className="md:col-span-6">
                  <p className="text-xs uppercase tracking-[0.25em] text-stone">{o.tag}</p>
                  <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-none mt-4">
                    {o.title}
                  </h2>
                  <p className="mt-6 text-base md:text-lg leading-relaxed text-ink/70 max-w-xl">
                    {o.copy}
                  </p>
                </div>
                <div className="md:col-span-4 flex flex-col justify-between gap-8">
                  <ul className="space-y-3">
                    {o.points.map((p) => (
                      <li key={p} className="flex gap-3 items-baseline text-sm md:text-base text-ink/75">
                        <span className="w-1.5 h-1.5 bg-moss shrink-0 translate-y-[-2px]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={o.to}
                    data-testid={`offering-cta-${i + 1}`}
                    className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-moss border-b border-moss/40 pb-1 w-fit hover:text-clay hover:border-clay transition-colors duration-300"
                  >
                    {o.cta}
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PHOTO BREAK */}
      <section className="border-b border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <div className="overflow-hidden border border-line">
                <img
                  src="/alex-online.jpg"
                  alt="Alex leading an online Maxwell Mastermind session"
                  loading="lazy"
                  className="w-full aspect-[16/10] object-cover"
                  data-testid="work-online-photo"
                />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <p className="font-serif text-3xl md:text-4xl tracking-tight leading-snug">
                In the room or across the world —{" "}
                <span className="italic text-moss">the work travels.</span>
              </p>
              <p className="mt-6 text-sm md:text-base text-ink/70 leading-relaxed">
                Coaching sessions, masterminds and The Messy Middle journey are
                available in person and online, so geography never has to be the
                reason you grow alone.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section data-testid="before-after-section" className="bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline className="text-paper/50">The Shift</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6 max-w-3xl">
              From stuck in the middle to{" "}
              <span className="italic text-clay">"maybe I can take another step."</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-px bg-paper/15 border border-paper/15">
            <Reveal className="bg-ink">
              <div className="p-8 md:p-12">
                <p className="text-xs uppercase tracking-[0.25em] text-paper/50 mb-8">Before</p>
                <ul className="space-y-5">
                  {before.map((b) => (
                    <li key={b} className="font-serif text-xl md:text-2xl text-paper/55 tracking-tight leading-snug border-b border-paper/10 pb-5">
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="bg-ink">
              <div className="p-8 md:p-12">
                <p className="text-xs uppercase tracking-[0.25em] text-clay mb-8">After</p>
                <ul className="space-y-5">
                  {after.map((a) => (
                    <li key={a} className="font-serif text-xl md:text-2xl text-paper tracking-tight leading-snug border-b border-paper/10 pb-5">
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* KIND WORDS */}
      <Testimonials />

      {/* HOW IT STARTS */}
      <section data-testid="how-it-starts" className="border-b border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline>How It Begins</Overline>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-line border border-line">
            {[
              ["I", "Tell me where you are", "A simple, honest message. No pitch, no pressure — just the truth about where you find yourself."],
              ["II", "We explore what's possible", "A conversation about your season, your hopes, and which path — coaching, the journey, or leadership work — actually fits."],
              ["III", "Take your next courageous step", "Small, real and doable. Growth begins the moment you decide not to go alone."],
            ].map(([n, t, c], i) => (
              <Reveal key={n} delay={i * 0.1} className="bg-paper">
                <div className="p-8 md:p-10 min-h-[240px] flex flex-col">
                  <span className="font-serif text-3xl text-clay">{n}.</span>
                  <h3 className="font-serif text-2xl tracking-tight mt-6">{t}</h3>
                  <p className="text-sm md:text-base text-ink/65 mt-4 leading-relaxed">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="work-cta-start-conversation"
              className="group mt-14 inline-flex items-center gap-3 bg-clay text-paper text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-ink transition-colors duration-300"
            >
              Start the Conversation
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
